import {
  Box,
  Grid,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AddSkillBook } from "./AddSkillBook";
import { getTabsProfile } from "./profile";
import {
  ProfileStack
} from "./Profile.styles";

import {
  CardProfile,
  CardRanking,
  CardUserGoals,
  GridMain,
  Tabs
} from "../../components";
import { useAnnouncement } from "../../components/Announcement/Announcement.component";
import { UserProgressWidget } from "../../components/UserProgressWidget/UserProgressWidget";
import { WidgetDrawer } from "../../components/WidgetDrawer/WidgetDrawer.component";
import { useAchievementsQuery } from "../../queries";
import { getApiUserInfo } from "../../services/auth";
import { useAuthStore } from "../../stores";

export function Profile() {
  const { t } = useTranslation();
  const { t: tAnnouncement } = useTranslation();
  const { announce } = useAnnouncement();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);
  const corporateInfo = useAuthStore(state => state.corporateInfo);

  const {
    data: learnerAchievements
  } = useAchievementsQuery({ refetchOnMount:"always" });

  const tabsProfileContents = getTabsProfile({
    onboarding:Boolean(corporateInfo?.onboarding),
    onboardingCapability:Boolean(corporateInfo?.onboarding_capability),
    onboardingExecutiveJob:Boolean(corporateInfo?.onboarding_executive_job),
    onboardingPersonalJob:Boolean(corporateInfo?.onboarding_personal_job),
    learnerAchievements:learnerAchievements||[]
  });


  const language = document.documentElement.lang;
  const userInfo = useAuthStore(state => state.userInfo);
  const setUserInfo = useAuthStore(state => state.setUserInfo);
  const sessionToken = useAuthStore(state => state.sessionToken);
  const session = useAuthStore(state => state.session);

  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  //Get translated user profession / job labels
  const loadSectorTranslations = () => {

    if(!sessionToken || !language || !userInfo) return;

    getApiUserInfo({
      accessToken: session?.getAccessToken().getJwtToken() ?? "",
      sessionToken,
      language
    }).then((res) => {
      if(res) {
        const updatedJob = res.job ?? userInfo?.job;
        const updatedProfession = res.profession ?? userInfo?.profession;

        setUserInfo({
          ...userInfo,
          job: updatedJob,
          profession: updatedProfession
        });
      }
    }).catch((error) => {
      console.error("An error occurred retrieving user info", error);
    });
  };

  useEffect(() => {
    loadSectorTranslations();
  }, [language]);

  useEffect(() => {
    announce( tAnnouncement("announcement_profile"));
  }, []);

  if (isDesktop) {
    return (
      <ProfileStack>
        <GridMain>
          <WidgetDrawer />
          <Box
            sx={ {
              display: "flex",
              flexDirection: "row",
              gap: 2,
              mt: 2,
              width: "100%"
            } }
          >
            { /* Colonna sinistra: tab + contenuto */ }
            <Box sx={ { flex: 1, minWidth: 0 } }>
              <Tabs contents={ tabsProfileContents }/>
              { (smartConfiguration && smartConfiguration.visExternalCertificate) && <AddSkillBook /> }
              <Box sx={ {
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                mt: 3
              } }>
                <Typography>{ t("version-label") } { import.meta.env.PACKAGE_VERSION }</Typography>
              </Box>
            </Box>

            { /* Colonna destra: widget */ }
            <ProfileWidgets />
          </Box>
        </GridMain>
      </ProfileStack>
    );
  }

  // Mobile / tablet: layout originale
  return (
    <ProfileStack>
      <GridMain>
        <ProfileWidgetsMobile />
        <Tabs contents={ tabsProfileContents }/>
        { (smartConfiguration && smartConfiguration.visExternalCertificate) && <AddSkillBook /> }
        <Box sx={ {
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          mt: 3
        } }>
          <Typography>{ t("version-label") } { import.meta.env.PACKAGE_VERSION }</Typography>
        </Box>
      </GridMain>
    </ProfileStack>
  );
}

// Desktop: widget in colonna verticale a destra
function ProfileWidgets() {
  const theme = useTheme();
  const userData = useAuthStore(state => state.userData);
  const userInfo = useAuthStore(state => state.userInfo);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  return (
    <Box
      id="profile-default-widget-container"
      sx={ {
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "24rem",
        flexShrink: 0
      } }
    >
      <CardProfile
        city={ userInfo?.birth_province || "" }
        email={ userData?.email || "" }
        location={ userInfo?.address || "" }
        name={ userData?.name || "" }
        phone={ userInfo?.phone_number || "" }
        surname={ userData?.surname || "" }
      />
      { smartConfiguration?.progressWidget && <UserProgressWidget/> }
      { smartConfiguration?.progressWidget && (
        <CardUserGoals
          color={ theme.customColors.textWidget }
          isProfilePage={ true }
        />
      ) }
      <CardRanking isUserPage={ true }/>
    </Box>
  );
}

// Mobile / tablet: layout originale con Grid
function ProfileWidgetsMobile() {
  const theme = useTheme();
  const userData = useAuthStore(state => state.userData);
  const userInfo = useAuthStore(state => state.userInfo);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      <WidgetDrawer />
      <Grid
        display={ "flex" }
        gap={ theme.spacing(2) }
        margin={ theme.spacing(5,0) }
        width="100%"
        id="profile-default-widget-container"
        sx={ {
          [theme.breakpoints.down("md")]:{
            "> div": {
              height:"auto"
            }
          },

          [theme.breakpoints.down("md")]:{
            flexDirection: "column",
            margin:`${theme.spacing(2)} auto 0`
          }
        } }
      >
        <Grid
          id="profile-progress-container"
          display={ isMobile ? "block" : "flex" }
          flexDirection={ "column" }
          gap={ "16px" }
          item
          xs={ 12 } sm={ 12 } md={ 12 } lg={ 7 }
          sx={ {
            [`${theme.breakpoints.down("sm")}`]:{
              maxWidth:"100%",
              minWidth:"unset",
              width:"100%",
              display: "flex",
              gap: theme.spacing(2)
            }
          } }
        >
          <CardProfile
            city={ userInfo?.birth_province || "" }
            email={ userData?.email || "" }
            location={ userInfo?.address || "" }
            name={ userData?.name || "" }
            phone={ userInfo?.phone_number || "" }
            surname={ userData?.surname || "" }
          />
          <Grid
            height={ "100%" }
            display={ isMedium ? "block" : "flex" }
            flexDirection={ isMobile ? "column" : "row" } gap={ 2 }

          >
            { smartConfiguration?.progressWidget && <UserProgressWidget/> }
            { smartConfiguration?.progressWidget && <CardUserGoals color={ theme.customColors.textWidget } isProfilePage={ true }/> }
          </Grid>
        </Grid>
        <Grid
          className="card-ranking"
          item
          xs={ 12 } sm={ 12 } md={ 12 } lg={ 5 }
          sx={ {
            ">div": {
              [`${theme.breakpoints.down("sm")}`]: {
                marginBottom: `${theme.spacing(2)}`
              },
              ".scrollable": {
                maxHeight: "100%"
              }
            },

            [theme.breakpoints.down("sm")]: {
              width:"100%"
            }
          } }
        >
          <CardRanking isUserPage={ true }/>
        </Grid>
      </Grid>
    </>
  );
}
